import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarClock, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { useSite } from "@/context/SiteContext";
import { isFramed, openTopLevel } from "@/lib/topNavigation";
import { CustomPlanCta } from "@/components/apps/PlanNotes";
import { usdNet } from "@/components/market/FxNote";

export const DURATIONS = [1, 3, 6, 12];
const FACTOR = { 1: 1, 3: 0.95, 6: 0.9, 12: 10 / 12 };
const r2 = (x) => Math.round((x + 1e-9) * 100) / 100;
export const MINISITE_HT = 79.99;

// Same grid as the backend (marketplace/prepaid.py): discount on the base only, extra employees always full price.
export const prepaidPrice = (base, months, employees = 0) => {
  const full = base * months;
  const baseTotal = months === 12 ? base * 10 : full * FACTOR[months];
  const total = r2(baseTotal + 10 * employees * months);
  return { months, total_ht: total, total_ttc: r2(total * 1.2), saving_ht: r2(full - baseTotal), monthly_after_ht: r2(base + 10 * employees) };
};

export const useFmt = () => {
  const { locale } = useI18n();
  return useMemo(() => {
    const mk = (cur) => {
      const f = new Intl.NumberFormat(locale, { style: "currency", currency: cur, currencyDisplay: "narrowSymbol", minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return (n) => f.format(n).replace(/ /g, "\u00a0").replace("US$", "$");
    };
    return { eur: mk("EUR"), usd: mk("USD") };
  }, [locale]);
};

export const DurationPicker = ({ value, onChange, testId = "duration-picker" }) => {
  const { t } = useI18n();
  return (
    <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-4" role="radiogroup" aria-label={t("prepaid.title")} data-testid={testId}>
      {DURATIONS.map((m) => (
        <button key={m} type="button" role="radio" aria-checked={value === m} onClick={() => onChange(m)}
          className={`min-h-[44px] min-w-0 rounded-xl border px-2 py-2 text-center text-xs font-bold leading-tight hyphens-none transition-colors duration-150 ${value === m ? "border-[var(--gold)] bg-[var(--gold)] text-black" : "border-[rgba(245,197,66,0.35)] bg-black/30 text-neutral-100 hover:border-[var(--gold)]"}`}
          data-testid={`${testId}-${m}`}>
          {t(`prepaid.m${m}`)}
        </button>
      ))}
    </div>
  );
};

// Total HT / TTC, saving, USD at today's rate, then the automatic monthly charge.
export const PrepaidSummary = ({ base, months, employees = 0, date, testId = "prepaid-summary" }) => {
  const { t, fmtDate } = useI18n();
  const { usd } = useSite();
  const fmt = useFmt();
  const q = prepaidPrice(base, months, employees);
  return (
    <div className="min-w-0 space-y-1 text-sm text-neutral-200" data-testid={testId}>
      <p className="font-semibold text-white" data-testid={`${testId}-total`}>{t("prepaid.total", { n: months, ht: fmt.eur(q.total_ht), ttc: fmt.eur(q.total_ttc) })}</p>
      {q.saving_ht > 0 && <p className="font-semibold text-emerald-300" data-testid={`${testId}-saving`}>{t("prepaid.save", { amount: fmt.eur(q.saving_ht) })}</p>}
      {usd?.rate && <p className="text-xs text-neutral-400" data-testid={`${testId}-usd`}>{t("prepaid.usd", { amount: fmt.usd(usdNet(q.total_ht, usd.rate)) })}</p>}
      {months > 1 && <p className="text-xs text-neutral-400" data-testid={`${testId}-then`}>{date ? t("prepaid.then", { amount: fmt.eur(q.monthly_after_ht), date: fmtDate(date) }) : t("prepaid.thenNoDate", { amount: fmt.eur(q.monthly_after_ht) })}</p>}
    </div>
  );
};

export const LongerPlanBox = ({ testId = "prepaid-longer" }) => <CustomPlanCta textKey="prepaid.longer" testId={testId} />;

// Mini-site presentation pages: 79,99 € HT per business, duration selector, honest employee note.
export const MinisitePricing = ({ testId = "minisite-pricing" }) => {
  const { t } = useI18n();
  const [months, setMonths] = useState(1);
  return (
    <section className="card-carbon min-w-0 space-y-3 rounded-2xl p-5" data-testid={testId}>
      <h2 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--gold-bright)]">{t("prepaid.minisiteTitle")}</h2>
      <DurationPicker value={months} onChange={setMonths} testId={`${testId}-duration`} />
      <PrepaidSummary base={MINISITE_HT} months={months} testId={`${testId}-summary`} />
      <p className="text-xs text-neutral-400">{t("prepaid.empNote")}</p>
      <LongerPlanBox testId={`${testId}-longer`} />
    </section>
  );
};

const Reminder = ({ until, testId }) => {
  const { t, fmtDate } = useI18n();
  return (
    <p className="flex items-start gap-2 rounded-xl border border-amber-400/60 bg-amber-950/50 px-4 py-3 text-sm text-amber-100" role="status" data-testid={testId}>
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {t("prepaid.reminder", { date: fmtDate(until) })}
    </p>
  );
};

// Checkout (developer billing card / client space): server quote with the real start/end dates, then one Stripe Checkout.
export const PrepaidCheckout = ({ billing, business, testId = "prepaid-checkout" }) => {
  const { t, fmtDate } = useI18n();
  const fmt = useFmt();
  const mini = Boolean(business);
  const [months, setMonths] = useState(3);
  const [emp, setEmp] = useState(business?.employees || 0);
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(null);
  const body = useMemo(() => ({ months, ...(mini ? { key_id: business.key_id, ref: business.ref, employees: emp } : {}) }), [months, mini, business, emp]);
  useEffect(() => {
    let off = false;
    api.post("/dev/billing/prepaid/quote", body).then(({ data }) => !off && setQ(data)).catch((err) => !off && setQ({ error: translateError(err, t) }));
    return () => { off = true; };
  }, [body, t]);
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dev/billing/prepaid/checkout", { ...body, origin_url: window.location.origin, expected_total: q?.quote?.pay_ttc });
      if (isFramed()) setUrl(data.checkout_url);
      openTopLevel(data.checkout_url);
    } catch (err) {
      toast.error(translateError(err, t));
      setBusy(false);
    }
  };
  const p = mini ? business.prepaid : billing?.prepaid;
  const quote = q?.quote;
  return (
    <div className="min-w-0 space-y-3 rounded-2xl border border-[rgba(245,197,66,0.3)] bg-black/30 p-4" data-testid={testId}>
      <p className="flex items-center gap-2 text-sm font-bold text-white"><CalendarClock className="h-4 w-4 shrink-0 text-[var(--gold)]" /> {t("prepaid.title")}{mini ? ` · ${business.ref}` : ""}</p>
      {p && <p className="text-xs font-semibold text-emerald-300" data-testid={`${testId}-badge`}>{t("prepaid.badge", { n: p.months, date: fmtDate(p.until) })}</p>}
      {p?.remind && <Reminder until={p.until} testId={`${testId}-reminder`} />}
      <DurationPicker value={months} onChange={setMonths} testId={`${testId}-duration`} />
      {mini && !business.live && (
        <label className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-neutral-200">
          {t("prepaid.employees")}
          <input type="number" min={0} max={50} value={emp} onChange={(e) => setEmp(Math.max(0, Math.min(50, Number(e.target.value) || 0)))}
            className="w-20 rounded-lg border border-neutral-700 bg-black/50 px-2 py-1 text-white" data-testid={`${testId}-employees`} />
        </label>
      )}
      {q?.error && <p className="text-sm text-amber-200" data-testid={`${testId}-error`}>{q.error}</p>}
      {quote && (
        <div className="min-w-0 space-y-1 text-sm text-neutral-200" data-testid={`${testId}-quote`}>
          <p className="font-semibold text-white" data-testid={`${testId}-total`}>{t("prepaid.total", { n: months, ht: fmt.eur(quote.total_ht), ttc: fmt.eur(quote.total_ttc) })}</p>
          {quote.saving_ht > 0 && <p className="font-semibold text-emerald-300">{t("prepaid.save", { amount: fmt.eur(quote.saving_ht) })}</p>}
          {quote.usd && <p className="text-xs text-neutral-400">{t("prepaid.usd", { amount: fmt.usd(quote.usd.total_ht) })}</p>}
          <p className="text-xs text-neutral-300" data-testid={`${testId}-first-monthly`}>{t("prepaid.then", { amount: fmt.eur(quote.monthly_after_ht), date: fmtDate(quote.first_monthly_at) })}</p>
          {q.live && <p className="text-xs text-neutral-400">{t("prepaid.renewNote")}</p>}
          {mini && <p className="text-xs text-neutral-400">{t("prepaid.empNote")}</p>}
        </div>
      )}
      <button type="button" className="btn-gold btn-sm min-h-[44px] w-full justify-center whitespace-normal sm:w-auto" onClick={pay}
        disabled={busy || !quote || (months === 1 && q?.live) || billing?.payments_available === false} data-testid={`${testId}-pay`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} {t(months === 1 ? "prepaid.payMonthly" : "prepaid.pay", { n: months })}
      </button>
      {url && <a href={url} target="_top" rel="noopener" className="btn-red btn-sm btn-cta w-full sm:w-auto" data-testid={`${testId}-open`}>{t("billing.openCheckout")}</a>}
      <LongerPlanBox testId={`${testId}-longer`} />
    </div>
  );
};

// Client space: one prepaid block per business (REF) of the mini-site client.
export const BusinessPrepaid = ({ billing }) => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/dev/billing/prepaid/businesses").then(({ data }) => setList(data)).catch(() => {}); }, []);
  if (!list.length || billing?.status === "vip") return null;
  return (
    <section className="min-w-0 space-y-3" data-testid="client-prepaid">
      {list.map((b) => <PrepaidCheckout key={`${b.key_id}-${b.ref}`} business={b} billing={billing} testId={`client-prepaid-${b.ref}`} />)}
    </section>
  );
};
